import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Download,
  ExternalLink,
  Eye,
  FileSpreadsheet,
  FileText,
  File as FileIcon,
} from 'lucide-react';
import { Button, Card, CardContent } from '@mlm/ui';
import { listResources, openResource, type MemberResource } from '../lib/api';
import { EmptyState } from '../components/empty-state';

/** Human file size — `null` for link resources, which have no bytes. */
function fmtSize(bytes: number | null): string | null {
  if (bytes == null) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const isPdf = (r: MemberResource) => r.mimeType === 'application/pdf';
const isSheet = (r: MemberResource) => !!r.mimeType?.includes('spreadsheetml');
const isDoc = (r: MemberResource) => !!r.mimeType?.includes('wordprocessingml');

function KindIcon({ resource }: { resource: MemberResource }) {
  const cls = 'h-5 w-5 shrink-0';
  if (resource.kind === 'link') return <ExternalLink className={`${cls} text-primary`} />;
  if (isSheet(resource)) return <FileSpreadsheet className={`${cls} text-success`} />;
  if (isPdf(resource) || isDoc(resource)) return <FileText className={`${cls} text-destructive`} />;
  return <FileIcon className={cls} />;
}

function ResourceCard({ resource }: { resource: MemberResource }) {
  const [busy, setBusy] = useState<'view' | 'download' | null>(null);
  const size = fmtSize(resource.sizeBytes);

  async function open(mode: 'view' | 'download') {
    setBusy(mode);
    try {
      await openResource(resource.id, mode);
    } finally {
      setBusy(null);
    }
  }

  return (
    <Card>
      <CardContent className="flex items-start gap-3 p-4">
        <KindIcon resource={resource} />
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium">{resource.title}</p>
          {resource.description ? (
            <p className="mt-0.5 text-sm text-muted-foreground">{resource.description}</p>
          ) : null}
          <p className="mt-1 text-xs text-muted-foreground">
            {resource.kind === 'link' ? 'External link' : (resource.originalFilename ?? 'Document')}
            {size ? ` · ${size}` : ''}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {resource.kind === 'link' ? (
              <Button size="sm" variant="outline" asChild>
                <a href={resource.linkUrl ?? '#'} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-4 w-4" /> Open
                </a>
              </Button>
            ) : (
              <>
                {/* Only PDFs render in-browser; Office files always download. */}
                {isPdf(resource) ? (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy !== null}
                    onClick={() => void open('view')}
                  >
                    <Eye className="h-4 w-4" /> {busy === 'view' ? 'Opening…' : 'View'}
                  </Button>
                ) : null}
                <Button size="sm" disabled={busy !== null} onClick={() => void open('download')}>
                  <Download className="h-4 w-4" /> {busy === 'download' ? 'Preparing…' : 'Download'}
                </Button>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function ResourcesPage() {
  const [category, setCategory] = useState<string | undefined>();
  const q = useQuery({
    queryKey: ['resources', category],
    queryFn: () => listResources(category),
  });

  const categories = q.data?.categories ?? [];
  const resources = q.data?.resources ?? [];

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">Resources</h1>
        <p className="text-sm text-muted-foreground">
          Forms, price lists and materials published by the team.
        </p>
      </div>

      {categories.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setCategory(undefined)}
            className={
              'rounded-full border px-3 py-1 text-sm transition-colors ' +
              (category === undefined
                ? 'border-primary bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-muted')
            }
          >
            All
          </button>
          {categories.map((c) => (
            <button
              key={c.id}
              onClick={() => setCategory(c.id)}
              className={
                'rounded-full border px-3 py-1 text-sm transition-colors ' +
                (category === c.id
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted')
              }
            >
              {c.name}
            </button>
          ))}
        </div>
      ) : null}

      {q.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : resources.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="Nothing here yet"
          description="Documents and links published by the team will appear here."
        />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {resources.map((r) => (
            <ResourceCard key={r.id} resource={r} />
          ))}
        </div>
      )}
    </div>
  );
}
